import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Shield, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'

export default function PlatformAdminLoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')
  const { loginFromToken } = useAuth()

  // SECURITY: Ensure this page only loads on admin subdomain
  React.useEffect(() => {
    const hostname = window.location.hostname
    if (hostname !== 'admin.housepoints.ai' && hostname !== 'localhost') {
      // Redirect non-admin domains
      window.location.href = '/'
    }
  }, [])

  React.useEffect(() => {
    // Check for OAuth token in URL (from centralized auth service)
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const googleLogin = urlParams.get('google_login')
    const appleLogin = urlParams.get('apple_login')
    const microsoftLogin = urlParams.get('microsoft_login')

    // Handle OAuth login from any provider
    if (token && (googleLogin || appleLogin || microsoftLogin)) {
      const provider = googleLogin ? 'Google' : appleLogin ? 'Apple' : 'Microsoft'

      // Clear the token from URL first
      window.history.replaceState({}, document.title, window.location.pathname)

      console.log(`Processing ${provider} OAuth token for platform admin`)

      // Validate token with backend and get user info
      loginFromToken(token).then((result) => {
        if (result.success) {
          toast.success(`${provider} login successful!`)
          navigate('/')
        } else {
          toast.error(result.error || 'Authentication failed. Please try again.')
        }
      }).catch((err) => {
        console.error(`${provider} login error:`, err)
        toast.error('Authentication failed. Please try again.')
      })
      return
    }

    // Show error if present
    if (error === 'google_auth_failed' || error === 'token_exchange_failed' || error === 'user_info_failed') {
      toast.error('Authentication failed. Please try again.')
    } else if (error === 'unauthorized_user' || error === 'invalid_state') {
      toast.error('Access denied. Please try again.')
    } else if (error === 'family_not_found') {
      // This is expected for admin subdomain - ignore it
      console.log('Ignoring family_not_found error for admin subdomain')
    }
  }, [error, navigate, loginFromToken])

  const handleGoogleLogin = async () => {
    try {
      // Redirect to platform-level OAuth endpoint (no family context required)
      // Send only the path, not the full URL
      const returnTo = '/login'
      const authServiceURL = `https://auth.housepoints.ai/api/auth/platform/google?redirect_uri=${encodeURIComponent(returnTo)}`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('OAuth init error:', error)
      toast.error('Failed to start Google authentication')
    }
  }

  const handleAppleLogin = async () => {
    try {
      const returnTo = window.location.origin + '/login'
      const authServiceURL = `https://auth.housepoints.ai/api/auth/apple?return_to=${encodeURIComponent(returnTo)}&admin=true`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('Apple OAuth init error:', error)
      toast.error('Failed to start Apple authentication')
    }
  }

  const handleMicrosoftLogin = async () => {
    try {
      const returnTo = window.location.origin + '/login'
      const authServiceURL = `https://auth.housepoints.ai/api/auth/microsoft?return_to=${encodeURIComponent(returnTo)}&admin=true`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('Microsoft OAuth init error:', error)
      toast.error('Failed to start Microsoft authentication')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-4 rounded-full">
              <Shield className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Admin</h1>
          <p className="text-gray-600">admin.housepoints.ai</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold mb-2">Welcome Back!</h2>
            <p className="text-gray-600">Sign in with your admin account</p>
          </div>

          <div className="space-y-3">
            {/* Google Sign In */}
            <button
              onClick={handleGoogleLogin}
              className="w-full bg-white border border-gray-300 rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-medium text-gray-700 text-lg">Continue with Google</span>
            </button>

            {/* Apple Sign In */}
            <button
              onClick={handleAppleLogin}
              className="w-full bg-black text-white border border-black rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-900 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="font-medium text-lg">Continue with Apple</span>
            </button>

            {/* Microsoft Sign In */}
            <button
              onClick={handleMicrosoftLogin}
              className="w-full bg-white border border-gray-300 rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 23 23">
                <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              <span className="font-medium text-gray-700 text-lg">Continue with Microsoft</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Only authorized platform administrators can access this system
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800">
                <strong>Platform Admin Access</strong><br />
                This login is restricted to platform administrators only.
                Unauthorized access attempts are logged and monitored.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
